import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemoSession } from '../../auth/DemoSessionProvider';
import { isJwtAuth } from '../../auth/authMode';
import { getTenant } from '../../auth/demoTenants';
import { FEATURES } from '../../config/features';
import { canAccessProviderAdmin } from '../../pages/provider/admin/providerAdminAccess';
import { WORKSPACE_IDS, getWorkspace } from '../../auth/workspaces';
import { IconChevronSquare } from '../ui/icons';

function Dropdown({ open, onClose, children }) {
  if (!open) return null;
  return (
    <>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div
        className="absolute right-0 top-full z-50 mt-2 w-[300px] overflow-hidden rounded-xl border border-eorig-border bg-white py-1 shadow-eorig-card"
        role="menu"
      >
        {children}
      </div>
    </>
  );
}

export function WorkspaceMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { session, signOut } = useDemoSession();

  if (!session) return null;

  const workspace = getWorkspace(session.workspaceId);
  const tenant = getTenant(session.tenantId);
  const jwtMode = isJwtAuth();
  const prefix =
    session.workspaceId === WORKSPACE_IDS.PROVIDER
      ? '/provider'
      : session.workspaceId === WORKSPACE_IDS.CUSTODIAN
        ? '/custodian'
        : '';
  const orgAdmin = session.membershipRole === 'admin' || session.isPlatformAdmin;

  function close() {
    setOpen(false);
  }

  function go(path) {
    close();
    navigate(path);
  }

  function handleSignOut() {
    close();
    signOut();
    navigate('/signin');
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${session.roleLabel} workspace`}
        className="flex items-center gap-2 rounded-lg py-0.5 pl-0.5 pr-1 transition-colors hover:bg-[#F8F8F8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/25 sm:pr-2"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-eorig-gradient text-[11px] font-bold text-white">
          {session.initials}
        </span>
        <span className="hidden min-w-0 text-left sm:block">
          <span className="block max-w-[140px] truncate text-[13px] font-semibold leading-4 text-[#212121]">
            {session.userDisplayName}
          </span>
          <span className="mt-0.5 block max-w-[140px] truncate text-[11px] leading-4 text-[#4D4D4D]">
            {session.organizationName ||
              tenant?.organizationName ||
              workspace?.userEmail ||
              session.roleLabel}
          </span>
        </span>
        <IconChevronSquare className="hidden h-4 w-4 text-[#003A70] sm:block" />
      </button>
      <Dropdown open={open} onClose={close}>
        <div className="border-b border-eorig-border px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-eorig-muted">
            Current workspace
          </p>
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-eorig-ink">
            <span className="h-1.5 w-1.5 rounded-full bg-eorig-cyan" />
            {session.organizationName}
          </p>
          <p className="mt-1 pl-3.5 text-xs text-eorig-ink">
            {session.roleLabel} · {session.userDisplayName}
          </p>
        </div>
        {jwtMode && orgAdmin ? (
          <div className="mt-1 border-t border-eorig-border">
            <button
              type="button"
              role="menuitem"
              onClick={() => go(`${prefix}/settings/members`)}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
            >
              Members
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go(`${prefix}/settings/invitations`)}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
            >
              Invitations
            </button>
          </div>
        ) : null}
        {FEATURES.providerAdminConsole && canAccessProviderAdmin(session) ? (
          <div className="border-t border-eorig-border">
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/provider/admin/organizations')}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
            >
              Admin organizations
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/provider/admin/programs')}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
            >
              Admin programs
            </button>
          </div>
        ) : null}
        {jwtMode && session.isPlatformAdmin ? (
          <div className="border-t border-eorig-border">
            <button
              type="button"
              role="menuitem"
              onClick={() => go(`${prefix}/admin/organizations`)}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
            >
              Organizations
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go(`${prefix}/admin/connections`)}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
            >
              Connections
            </button>
          </div>
        ) : null}
        <div className="mt-1 border-t border-eorig-border">
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className="flex w-full items-center px-3 py-2 text-left text-sm text-eorig-ink transition-colors hover:bg-eorig-surface"
          >
            Sign out
          </button>
        </div>
      </Dropdown>
    </div>
  );
}
